import React, { useEffect, useRef, useState } from 'react';
import { aboutContent } from '../data/mockData';
import { useTranslation } from "react-i18next";
import scopeImage from '../assets/about_imag.jpeg';
import qualityImage from '../assets/q.png';
import injections1 from '../assets/Injections1.png';
import PageLoader from '../components/PageLoader';
import usePageLoader from '../hooks/PageLoader';

const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const CountUp = ({ end, duration = 1800 }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setStarted(true); obs.disconnect(); }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!started) return;
    let start = 0;
    const steps = duration / 20;
    const inc = end / steps;
    const timer = setInterval(() => {
      start += inc;
      if (start >= end) { clearInterval(timer); setCount(end); }
      else setCount(Math.floor(start));
    }, 20);
    return () => clearInterval(timer);
  }, [started, end, duration]);
  return <span ref={ref}>{count}</span>;
};

const AboutPage = () => {
  const [historyLeftRef, historyLeftVis] = useReveal(0);
  const [historyRightRef, historyRightVis] = useReveal(150);
  const [statsRef, statsVis] = useReveal(0);
  const [visionLeftRef, visionLeftVis] = useReveal(0);
  const [visionRightRef, visionRightVis] = useReveal(180);
  const [missionLeftRef, missionLeftVis] = useReveal(0);
  const [missionRightRef, missionRightVis] = useReveal(180);
  const { t } = useTranslation();

  return (
    <div className="about-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes heroFadeIn { from { opacity:0; transform:translateY(32px); } to { opacity:1; transform:translateY(0); } }

        .about-page { font-family: 'Outfit', sans-serif; }

        /* ══ HERO ══ */
        .ap-hero {
          position: relative; min-height: 340px;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
          background: linear-gradient(135deg, #0d2233 0%, #1a3a4a 60%, #0f2d3d 100%);
        }
        .ap-hero-img {
          position: absolute; inset: 0;
          width: 100%; height: 100%; object-fit: cover; opacity: 0.22;
        }
        .ap-hero-grid {
          position: absolute; inset: 0; pointer-events: none;
          background-image: linear-gradient(rgba(58,143,163,0.07) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(58,143,163,0.07) 1px, transparent 1px);
          background-size: 56px 56px;
        }
        .ap-hero-line {
          position: absolute; top: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, transparent, #3a8fa3, #5aab9a, #3a8fa3, transparent);
        }
        .ap-hero-content {
          position: relative; z-index: 2; text-align: center; padding: 80px 24px 70px;
          animation: heroFadeIn 0.9s cubic-bezier(0.4,0,0.2,1) both;
        }
        .ap-hero-eyebrow {
          display: inline-block; font-size: 0.75rem; font-weight: 600;
          letter-spacing: 0.14em; text-transform: uppercase; color: #5aab9a;
          background: rgba(90,171,154,0.12); border: 1px solid rgba(90,171,154,0.3);
          padding: 6px 20px; border-radius: 999px; margin-bottom: 22px;
        }
        .ap-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2.8rem, 6vw, 5rem); font-weight: 800;
          color: #fff; line-height: 1.15; margin: 0;
          text-shadow: 0 2px 24px rgba(0,0,0,0.3);
        }

        /* ══ HISTORY ══ */
        .ap-history {
          background: linear-gradient(180deg, #e8f5f7 0%, #d4eef2 50%, #e0f4f7 100%);
          padding: 90px 0 80px; position: relative; overflow: hidden;
        }
        .ap-history::before {
          content: ''; position: absolute; inset: 0;
          background-image: radial-gradient(circle, rgba(58,143,163,0.06) 1px, transparent 1px);
          background-size: 36px 36px;
        }
        .ap-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; position: relative; z-index: 1; }
        .ap-history-grid {
          display: grid; grid-template-columns: 1fr; gap: 60px; align-items: center;
        }
        @media (min-width: 1024px) { .ap-history-grid { grid-template-columns: 1fr 1fr; } }

        .ap-img-wrap {
          opacity: 0; transform: translateX(-40px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        .ap-img-wrap.in { opacity: 1; transform: translateX(0); }

        .ap-img-single {
          border-radius: 24px; overflow: hidden;
          box-shadow: 0 20px 56px rgba(58,143,163,0.18), 0 6px 20px rgba(0,0,0,0.08);
          transition: transform 0.4s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s;
        }
        .ap-img-single:hover {
          transform: translateY(-8px);
          box-shadow: 0 32px 72px rgba(58,143,163,0.24), 0 10px 28px rgba(0,0,0,0.1);
        }
        .ap-img-single img { width: 100%; height: 420px; object-fit: cover; display: block; }

        .ap-history-text {
          opacity: 0; transform: translateX(40px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1) 0.15s;
        }
        .ap-history-text.in { opacity: 1; transform: translateX(0); }

        .ap-section-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 3.5vw, 2.8rem); font-weight: 800;
          color: #1a3a4a; margin: 0 0 24px; line-height: 1.2;
        }
        .ap-body-text {
          font-size: 1rem; color: #3d5a6a; line-height: 1.9;
          margin-bottom: 20px; text-align: justify;
        }

        /* ══ STATS ══ */
        .ap-stats {
          background: linear-gradient(180deg, #d4eef2 0%, #c8e8ed 100%);
          padding: 60px 0;
        }
        .ap-stats-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
          max-width: 900px; margin: 0 auto; padding: 0 32px;
        }
        @media (max-width: 640px) { .ap-stats-grid { grid-template-columns: 1fr; max-width: 360px; } }

        .ap-stat-card {
          background: rgba(255,255,255,0.72);
          backdrop-filter: blur(16px) saturate(1.5); -webkit-backdrop-filter: blur(16px) saturate(1.5);
          border: 1px solid rgba(255,255,255,0.88);
          border-radius: 28px; padding: 36px 24px; text-align: center;
          box-shadow: 0 4px 24px rgba(58,143,163,0.12), inset 0 1px 0 rgba(255,255,255,0.9);
          transition: all 0.35s cubic-bezier(0.4,0,0.2,1);
          opacity: 0; transform: translateY(32px);
          position: relative; overflow: hidden;
        }
        .ap-stat-card::before {
          content: ''; position: absolute; top: 0; left: 20%; right: 20%; height: 2px;
          background: linear-gradient(90deg, transparent, #3a8fa3, #5aab9a, transparent);
        }
        .ap-stat-card.in { opacity: 1; transform: translateY(0); }
        .ap-stat-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 48px rgba(58,143,163,0.22);
          border-color: rgba(58,143,163,0.3);
        }
        .ap-stat-val {
          font-family: 'Playfair Display', serif;
          font-size: 3rem; font-weight: 800; color: #1a3a4a;
          line-height: 1; margin-bottom: 10px;
        }
        .ap-stat-lbl { font-size: 0.9rem; color: #5a7d8a; font-weight: 500; }

        /* ══ VISION / MISSION ══ */
        .ap-vision, .ap-mission {
          background: linear-gradient(180deg, #e0f4f7 0%, #d4eef2 100%);
          padding: 80px 0; position: relative; overflow: hidden;
        }
        .ap-mission { background: linear-gradient(180deg, #d4eef2 0%, #c8e8ed 100%); }
        .ap-vm-grid {
          display: grid; grid-template-columns: 1fr; gap: 60px; align-items: center;
        }
        @media (min-width: 1024px) { .ap-vm-grid { grid-template-columns: 1fr 1fr; } }

        .ap-slide-left  { opacity:0; transform:translateX(-40px); transition: all 0.85s cubic-bezier(0.4,0,0.2,1); }
        .ap-slide-right { opacity:0; transform:translateX(40px);  transition: all 0.85s cubic-bezier(0.4,0,0.2,1) 0.15s; }
        .ap-slide-left.in  { opacity:1; transform:translateX(0); }
        .ap-slide-right.in { opacity:1; transform:translateX(0); }

        .ap-vm-img-wrap {
          border-radius: 24px; overflow: hidden;
          box-shadow: 0 20px 56px rgba(58,143,163,0.18), 0 6px 20px rgba(0,0,0,0.08);
          transition: transform 0.4s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s;
          position: relative;
        }
        .ap-vm-img-wrap::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(to bottom, transparent 60%, rgba(13,34,51,0.15) 100%);
        }
        .ap-vm-img-wrap:hover {
          transform: translateY(-8px);
          box-shadow: 0 32px 72px rgba(58,143,163,0.24), 0 10px 28px rgba(0,0,0,0.1);
        }
        .ap-vm-img-wrap img { width: 100%; height: 360px; object-fit: cover; display: block; }

        .ap-vm-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(58,143,163,0.1); border: 1px solid rgba(58,143,163,0.25);
          border-radius: 999px; padding: 6px 18px; margin-bottom: 18px;
          font-size: 0.75rem; font-weight: 600; letter-spacing: 0.12em;
          text-transform: uppercase; color: #3a8fa3;
        }
        .ap-vm-badge-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: linear-gradient(135deg, #3a8fa3, #5aab9a);
          box-shadow: 0 0 6px rgba(58,143,163,0.5);
        }

        .ap-blob { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(80px); }
        .ap-blob-1 { width:400px; height:400px; background:rgba(58,143,163,0.08); top:-100px; right:-100px; }
        .ap-blob-2 { width:300px; height:300px; background:rgba(90,171,154,0.08); bottom:-80px; left:-80px; }
      `}</style>

      {/* ── HERO ── */}
      <section className="ap-hero">
        <img src={qualityImage} alt="" className="ap-hero-img" />
        <div className="ap-hero-grid" />
        <div className="ap-hero-line" />
        <div className="ap-hero-content">
          <div className="ap-hero-eyebrow">{t("aboutPage.hero.eyebrow")}</div>
          <h1 className="ap-hero-title">{t("aboutPage.hero.title")}</h1>
        </div>
      </section>

      {/* ── HISTORY ── */}
      <section className="ap-history">
        <div className="ap-blob ap-blob-1" /><div className="ap-blob ap-blob-2" />
        <div className="ap-inner">
          <div className="ap-history-grid">
            <div ref={historyLeftRef} className={`ap-img-wrap ${historyLeftVis ? 'in' : ''}`}>
              <div className="ap-img-single">
                <img src={injections1} alt="Our Story" />
              </div>
            </div>
            <div ref={historyRightRef} className={`ap-history-text ${historyRightVis ? 'in' : ''}`}>
              <h2 className="ap-section-title">{t("aboutPage.history.title")}</h2>
              <p className="ap-body-text">{t("aboutPage.history.p1")}</p>
              <p className="ap-body-text">{t("aboutPage.history.p2")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="ap-stats">
        <div className="ap-stats-grid" ref={statsRef}>
          {[
            { val: 500, suffix: '+', lbl: t('aboutPage.stats.molecules') },
            { val: 12,  suffix: '+', lbl: t('aboutPage.stats.globalPresence') },
            { val: 100, suffix: '+', lbl: t('aboutPage.stats.yearsInnovation') }
          ].map((s, i) => (
            <div key={i} className={`ap-stat-card ${statsVis ? 'in' : ''}`}
              style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="ap-stat-val"><CountUp end={s.val} />{s.suffix}</div>
              <div className="ap-stat-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── VISION ── */}
      <section className="ap-vision">
        <div className="ap-blob ap-blob-2" />
        <div className="ap-inner">
          <div className="ap-vm-grid">
            <div ref={visionLeftRef} className={`ap-slide-left ${visionLeftVis ? 'in' : ''}`}>
              <div className="ap-vm-badge"><div className="ap-vm-badge-dot" /> {t("aboutPage.vision.badge")}</div>
              <h2 className="ap-section-title">{t("aboutPage.vision.title")}</h2>
              <p className="ap-body-text">{t("aboutPage.vision.description")}</p>
            </div>
            <div ref={visionRightRef} className={`ap-slide-right ${visionRightVis ? 'in' : ''}`}>
              <div className="ap-vm-img-wrap">
                <img src={scopeImage} alt="Vision" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section className="ap-mission">
        <div className="ap-blob ap-blob-1" />
        <div className="ap-inner">
          <div className="ap-vm-grid">
            <div ref={missionLeftRef} className={`ap-slide-left ${missionLeftVis ? 'in' : ''}`}>
              <div className="ap-vm-img-wrap">
                <img src={qualityImage} alt="Mission" />
              </div>
            </div>
            <div ref={missionRightRef} className={`ap-slide-right ${missionRightVis ? 'in' : ''}`}>
              <div className="ap-vm-badge"><div className="ap-vm-badge-dot" />{t("aboutPage.mission.badge")}</div>
              <h2 className="ap-section-title">{t("aboutPage.mission.title")}</h2>
              <p className="ap-body-text">{t("aboutPage.mission.description")}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;